import classNames from "classnames";
import { Controller, useForm } from "react-hook-form";
import Button from "../../atomic/Button";
import Input from "../FormElements/Input";
import { LoanApplicationService } from "../../../services/LoanApplication";
import DropdownWithSearch from "../DropdownWithSearch";
import { formatNumberInput } from "../../../shared/utils/Global";

export const RequiredIcon = () => (
  <svg
    stroke="currentColor"
    fill="currentColor"
    strokeWidth="0"
    viewBox="0 0 24 24"
    className="mr-1 mt-0.5 w-3 h-3"
    height="1em"
    width="1em"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"></path>
  </svg>
);

const detectInputType = (fieldName: string): string => {
  const name = fieldName.toLowerCase();
  const hasAny = (tokens: string[]) =>
    tokens.some((token) => name.includes(token));

  switch (true) {
    case hasAny(["email"]):
      return "email";
    case hasAny(["phone", "mobile", "contact"]):
      return "tel";
    case hasAny(["date"]):
      return "date";
    case hasAny(["amount", "number", "id"]):
      return "number";
    case hasAny(["time"]):
      return "time";
    case hasAny(["url", "link"]):
      return "url";
    default:
      return "text";
  }
};

interface FieldConfig {
  id: string;
  value: string;
  label: string;
  key: string;
  isRequired?: boolean;
  inputType?: string;
  allowedValues?: string[];
}

export default function EditFieldsDataConfig({
  onClose,
  fields,
  defaultFieldValues,
  isLenderPlatform,
  userID,
  loanApplicationID,
  sectionID,
  resourceName,
  resourceNameSingleSubmit,
  selectedCoApplicantUserID,
  token,
  pageName,
  notify,
  reload
}: {
  onClose: () => void;
  fields: Record<string, any>;
  defaultFieldValues: Record<string, any>;
  isLenderPlatform: boolean;
  userID: string;
  loanApplicationID: string;
  sectionID: string;
  resourceName: string;
  resourceNameSingleSubmit: string;
  selectedCoApplicantUserID?: string;
  token: string;
  pageName: string;
  notify: any;
  reload: (userID: string) => void;
}) {
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid }
  } = useForm({ defaultValues: defaultFieldValues });

  const onSubmit = async (data: Record<string, any>) => {
    const response = await LoanApplicationService.submitDataFromDashboard({
      token,
      pageName,
      isLenderPlatform,
      payload: {
        userID: selectedCoApplicantUserID || userID,
        loanApplicationID,
        resourceName: resourceNameSingleSubmit,
        sectionID,
        data: {
          ...data
        }
      }
    });

    if (response?.status) {
      notify({
        title: "Success",
        type: "success",
        text:
          response?.data?.message ||
          response?.error ||
          "Fields updated successfully"
      });
      setTimeout(() => onClose(), 500);
      reload(userID);
    } else {
      notify({
        title: "Error",
        type: "error",
        text:
          response?.error ||
          response?.data?.message ||
          response?.data?.error ||
          "Fields update failed"
      });
    }
  };

  const handleFormSubmit = (data: Record<string, any>) => {
    onSubmit(data);
  };

  const resetHandler = () => {
    const resetValues = {} as any;
    fields.forEach((field: any) => {
      const value = field.value;
      resetValues[field.key] = field.value || null; //typeof value === "object" ? "" : value || null;
    });

    reset(resetValues);
  };

  return (
    <>
      <form
        id="edit-fields-form"
        onSubmit={handleSubmit(handleFormSubmit)}
        className="space-y-4 max-h-[60vh] overflow-y-auto px-4 pb-4"
      >
        {fields.map(
          (
            {
              id,
              value,
              label,
              key,
              isRequired,
              inputType,
              allowedValues
            }: FieldConfig,
            index: number
          ) => {
            switch (inputType) {
              case "number":
                return (
                  <fieldset
                    key={id}
                    className={classNames("p-1 mt-3 mb-6")}
                  >
                    <Controller
                      name={key}
                      control={control}
                      rules={{
                        required: isRequired ? `${label} is required` : false,
                        validate: (val) => {
                          if (!isRequired) return true;
                          return (
                            val?.toString().trim().length > 0 ||
                            `${label} is required`
                          );
                        }
                      }}
                      render={({ field: { onChange, value, ...field } }) => (
                        <Input
                          id={label}
                          type="text"
                          label={label}
                          requiredIcon={isRequired}
                          value={formatNumberInput(value)}
                          onChange={(e) => {
                            const raw = e.target.value.replace(/,/g, "");
                            if (!isNaN(raw)) {
                              onChange(raw);
                            }
                          }}
                          error={!!errors[key]}
                          errorText={errors[key]?.message as string}
                          {...field}
                        />
                      )}
                    />
                  </fieldset>
                );

              case "text":
                return (
                  <fieldset
                    key={id}
                    className={classNames("p-1 mt-3 mb-6")}
                  >
                    <Input
                      id={label}
                      type={inputType || "text"}
                      label={label}
                      requiredIcon={isRequired}
                      register={register(key, {
                        required: isRequired ? `${label} is required` : false,
                        validate: (val) => {
                          if (!isRequired) return true;
                          return (
                            val?.toString().trim().length > 0 ||
                            `${label} is required`
                          );
                        }
                      })}
                      error={!!errors[key]}
                      errorText={errors[key]?.message as string}
                    />
                  </fieldset>
                );

              case "dropdown":
                const dropdownOptions = (allowedValues || []).map((item) => ({
                  name: item.label,
                  value: item.value
                }));

                const selectedObj = dropdownOptions.find(
                  (opt) => opt.value === value
                );

                return (
                  <fieldset
                    key={id}
                    className="p-1 mt-3 mb-6 w-full"
                  >
                    <Controller
                      control={control}
                      name={key}
                      rules={{
                        required: isRequired ? `${label} is required` : false
                      }}
                      render={({
                        field: { onChange, value },
                        fieldState: { error }
                      }) => (
                        <DropdownWithSearch
                          options={dropdownOptions}
                          hasSearch={false}
                          label={label}
                          styles={{
                            container: "w-full"
                          }}
                          placeholder={label}
                          selectedItems={selectedObj}
                          onChange={(selectedOption) => {
                            onChange(selectedOption.value);
                          }}
                          error={!!error}
                          errorText={error?.message}
                          requiredIcon={isRequired}
                        />
                      )}
                    />
                  </fieldset>
                );
            }
          }
        )}
      </form>

      <div className="items-center shrink-0 border-t border-gray-200 p-3">
        <fieldset className="flex items-center justify-end gap-2">
          <Button
            onClick={resetHandler}
            className="text-sm mr-4 px-4 py-2 w-auto rounded-lg bg-white !border !border-solid !border-gray-300 text-gray-700 flex items-center justify-center font-medium"
          >
            Reset
          </Button>
          <Button
            form="edit-fields-form"
            type="submit"
            color="primary"
          >
            Save
          </Button>
        </fieldset>
      </div>
    </>
  );
}
